import { Progress, Tag } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { STATUS_NAME } from '@/pages/project-management/detail/constant';
import { IProcess, ITask, ITaskFormFilter } from '@/pages/project-management/detail/utils';
import { getStatusTask, isComplete, renderDataTask, renderTaskStatus } from '@/pages/project-management/detail/utils/helper';
import { PROCESS_TRAVEL_NAME } from '@pages/project-management/project-list-v2/models';

import BaseTable from '@/components/table/BaseTable';

import useAuthorization from '@/hooks/useAuthorization';
import useFetch from '@/hooks/useFetch';

import { DataInput, DataViewer } from '@/utils/helpers/common';
import { MOT_OTHER_GROUPS_OPTION, MOT_OTHER_TASK_TYPE, TODO_SUPPORT_GROUPS_OPTION, TODO_SUPPORT_TASK_TYPE } from '@/utils/interfaces';
import { Ii18n } from '@/utils/interfaces/i18n';
import { CATEGORY } from '@utils/constants';

import InforIcon from '@/assets/icons/ic_info.svg';

import RenderTagTaskList from './RenderTagTaskFilter';
import TaskFilter from './TaskFilter';
import TaskTable from './TaskTable';

interface ITaskListProps {
  listProjectProcess: IProcess[];
  fetchData?: () => void;
  dataDetail?: any[];
}

const TASK_SUPPORT_USER_ID = 'efe031d6-0b82-4ae0-98dc-ef211298ff9c';

const TaskList = ({ listProjectProcess, fetchData, dataDetail }: ITaskListProps) => {
  const { t }: Ii18n = useTranslation();
  const { id } = useParams();
  const { isInternalRole } = useAuthorization();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const isTaskSupport =
    isInternalRole && listProjectProcess.some((process) => process.projectTasks?.some((task) => task.picIds?.includes(TASK_SUPPORT_USER_ID)));
  const taskListUrl = `/prj/projectTask/${id}/tasks/assigned${isTaskSupport ? '?isTaskSupport=true' : ''}`;
  const { data } = useFetch<any>(taskListUrl, 'GET');

  const [tasksList, setTasksList] = useState<any[]>([]);
  const [overviewAllTask, setOverviewAllTask] = useState({
    totalTaskCompleted: 0,
    totalTask: 0,
    includeTaskDelay: false
  });

  const formFilter = useForm<ITaskFormFilter>({
    defaultValues: {
      category: [],
      status: null,
      deadlineDateFrom: null,
      deadlineDateTo: null,
      picIds: null
    }
  });

  const formValues = formFilter.watch();

  const primaryValues = formFilter.watch(['category', 'status', 'deadlineDateFrom', 'deadlineDateTo', 'picIds']);
  const [category, status, deadlineDateFrom, deadlineDateTo, picIds] = formFilter.watch([
    'category',
    'status',
    'deadlineDateFrom',
    'deadlineDateTo',
    'picIds'
  ]);

  const isTravelProcess = (item: IProcess) => item.processName === PROCESS_TRAVEL_NAME;

  const totalFiltered = () => {
    let counter = 0;
    primaryValues.forEach((value, index) => {
      if ((!index || index === primaryValues.length - 1) && !value?.length) return;
      if (value) counter++;
    });
    return counter || 0;
  };

  const filterTask = (tasks: ITask[], filter: ITaskFormFilter) => {
    return tasks.filter((task) => {
      const convertDeadlineDate = DataInput.formatTime(task.deadline);
      if (!isInternalRole && TODO_SUPPORT_GROUPS_OPTION.includes(task.taskType as TODO_SUPPORT_TASK_TYPE)) {
        return false;
      }
      if (filter.category?.length && !filter.category.includes(task.taskType)) {
        return false;
      }
      if (filter.status && filter.status !== task.statusName) {
        return false;
      }
      if (filter.deadlineDateFrom && convertDeadlineDate && convertDeadlineDate < filter.deadlineDateFrom) {
        return false;
      }
      if (filter.deadlineDateTo && convertDeadlineDate && convertDeadlineDate > filter.deadlineDateTo) {
        return false;
      }
      if ((filter.deadlineDateFrom || filter.deadlineDateTo) && !convertDeadlineDate) {
        return false;
      }
      if (filter?.picIds?.length) {
        if (task?.picIds?.length) {
          const picIdsFilter = filter.picIds;
          const picIdsTask = task.picIds;
          return picIdsTask.some((item) => picIdsFilter.includes(item));
        } else return false;
      }
      return true;
    });
  };

  const convertListTask = (listTask: ITask[]) => {
    for (const item of listTask) {
      if (!item?.pics?.length) continue;
      let picName = '';
      let picIds = [];
      for (const key in item.pics) {
        const pic = item.pics[key];
        if (isNil(pic?.picStatus)) continue;
        picIds.push(pic.picId);
        if (pic.picStatus) picName += pic.picName;
        else picName += `(${t('account_list:status_account:inactive')}) ${pic.picName}`;

        if (Number(key) !== item.pics.length - 1) picName += ', ';
      }

      item.picName = picName;
      item.picIds = picIds;
    }

    return listTask;
  };

  const picOptions = useMemo(() => {
    return data?.map((item: { picId: string; name: string; status: boolean }) => ({
      label: item.status ? item.name : `(${t('account_list:status_account:inactive')}) ${item.name}`,
      value: item.picId
    }));
  }, [data]);

  const TagDate = (props: { date?: string }) => {
    const { date } = props;
    return (
      <div className='px-[16px] py-[8px] bg-gray1 border-[1px] border-solid border-gray2 rounded-[8px] text-14 text-textGray min-w-[109px] text-center'>
        {DataViewer.displayTime(date)}
      </div>
    );
  };

  const setFilterTaskDelay = () => {
    formFilter.setValue('status', STATUS_NAME.DELAYED);
  };

  const handleOnExpand = (expanded: boolean, record: IProcess) => {
    const newExpanded = [...expandedKeys];
    if (expanded && record.rowKey) {
      newExpanded.push(record.rowKey);
    } else {
      const matchIndex = newExpanded.findIndex((key) => key === record.rowKey);
      if (matchIndex !== -1) newExpanded.splice(matchIndex, 1);
    }
    setExpandedKeys(newExpanded);
  };

  const handleRowClass = (record: IProcess, index: number) => {
    let className: string = `process-row-${index}`;

    if (isTravelProcess(record)) {
      className += ' process-row-disable';
    }
    return className;
  };

  const toggleProcessByName = (record: IProcess) => {
    if (!record.rowKey) {
      return;
    }
    if (expandedKeys.includes(record.rowKey)) {
      setExpandedKeys(expandedKeys.filter((key) => key !== record.rowKey));
      return;
    }
    setExpandedKeys([...expandedKeys, record.rowKey]);
  };

  const generateProcessColumns = () => {
    return [
      {
        width: 'calc(100%)',
        render: (_: string, record: IProcess) => {
          const summaryData = record.summaryData;
          const listTaskTodo = summaryData.groups[CATEGORY.CS] ?? [];
          const listTaskSupport = summaryData.groups[CATEGORY.SUPPORT] ?? [];
          const listTaskMOT = summaryData.groups[CATEGORY.MOT] ?? [];
          const listTaskOther = summaryData.groups[CATEGORY.OTHER] ?? [];
          const listTaskTodoCompleted = listTaskTodo.filter(isComplete);
          const listTaskSupportCompleted = listTaskSupport.filter(isComplete);
          const listTaskMOTCompleted = listTaskMOT.filter(isComplete);
          const listTaskOtherCompleted = listTaskOther.filter(isComplete);
          const listTask = record.projectTasks;

          const isTravel = isTravelProcess(record);
          const taskTags = () => {
            if (isTravel) {
              return null;
            }
            return isInternalRole ? (
              <>
                {!!(listTaskTodo.length + listTaskSupport.length) && (
                  <Tag color='#DBDBE0' className='text-[14px]' style={{ color: '#12212E', fontWeight: 700 }}>
                    CS, {t('task:type_support')}: {listTaskTodoCompleted.length + listTaskSupportCompleted.length}/
                    {listTaskTodo.length + listTaskSupport.length}
                  </Tag>
                )}
                {!!(listTaskMOT.length + listTaskOther.length) && (
                  <Tag color='#DBDBE0' className='text-[14px] ' style={{ color: '#12212E', fontWeight: 700 }}>
                    {t('template_layout:other_category')}, MOT: {listTaskMOTCompleted.length + listTaskOtherCompleted.length}/
                    {listTaskMOT.length + listTaskOther.length}
                  </Tag>
                )}
              </>
            ) : (
              !!listTask.length && (
                <Tag color='#DBDBE0' className='text-[14px]' style={{ color: '#12212E', fontWeight: 700 }}>
                  {listTaskTodoCompleted.length + listTaskMOTCompleted.length + listTaskOtherCompleted.length}/{listTask.length}
                </Tag>
              )
            );
          };

          return (
            <div className={classNames('flex justify-between py-[2px] pr-[4px]', isTravel && '-ml-[40px]')}>
              <div className='flex items-center'>
                <button
                  className='mr-[8px] !p-0 border-none bg-[transparent] cursor-pointer text-lnk font-bold'
                  onClick={() => toggleProcessByName(record)}
                >
                  {record?.processName ?? ''}
                </button>
                {taskTags()}
              </div>
              {!isTravel && (
                <div className='flex items-center gap-[32px] font-medium'>
                  <div className='flex items-center gap-[8px]'>
                    <span>{t('project_detail:task_list:completed_date_plan')}</span>
                    <span>
                      <TagDate date={summaryData.maxDeadlineDate} />
                    </span>
                  </div>
                  <div className='flex items-center gap-[8px]'>
                    <span>{t('project_detail:task_list:start_date')}</span>
                    <span>
                      <TagDate date={summaryData.minStartDate} />
                    </span>
                  </div>
                  <div className='flex items-center gap-[8px]'>
                    <span>{t('project_detail:task_list:completed_date_actual')}</span>
                    <span>
                      <TagDate date={summaryData.maxCompletedDate} />
                    </span>
                  </div>
                  {record.statusName ? (
                    <div>{renderTaskStatus(record.statusName)}</div>
                  ) : (
                    <div className='w-[80px] flex justify-center items-center text-textGray border-gray2 bg-gray1 text-[14px] font-medium border-[1px] border-solid rounded-[4px]'>
                      --
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }
      }
    ];
  };

  useEffect(() => {
    const newExpanded = [...expandedKeys];
    const isCheckAllTaskType = true;
    // Filter remove process '渡航' from expandedKeys
    listProjectProcess
      .filter((process) => !isTravelProcess(process))
      .forEach((item: IProcess) => {
        const filteredTasksByRole = isInternalRole
          ? item.projectTasks
          : item.projectTasks.filter((item) => MOT_OTHER_GROUPS_OPTION.includes(item.taskType as MOT_OTHER_TASK_TYPE));
        const status = getStatusTask(filteredTasksByRole, isCheckAllTaskType);
        if ([STATUS_NAME.TODO.toString(), STATUS_NAME.DELAYED.toString(), STATUS_NAME.IN_PROGRESS.toString()].includes(status)) {
          newExpanded.push(item.id);
        }
      });
    setExpandedKeys(newExpanded);
  }, [listProjectProcess]);

  useEffect(() => {
    const { newList, totalTask, totalTaskCompleted, includeTaskDelay } = handleDataTask();
    setTasksList(newList);
    setOverviewAllTask({
      totalTask,
      totalTaskCompleted,
      includeTaskDelay
    });
  }, [category, status, deadlineDateFrom, deadlineDateTo, picIds, listProjectProcess]);

  const handleDataTask = () => {
    let totalTask = 0;
    const filterValueProjectTasks = {
      totalTaskCompleted: 0,
      totalTaskDelay: 0
    };
    const newList: any[] = [];

    listProjectProcess.forEach((item) => {
      const { projectTasks } = item;
      if (isInternalRole) totalTask += projectTasks?.length;
      else totalTask += projectTasks.filter((item) => MOT_OTHER_GROUPS_OPTION.includes(item.taskType as MOT_OTHER_TASK_TYPE)).length;

      // Convert and filter tasks based on form values
      const filteredTasks = filterTask(convertListTask(projectTasks), formValues);

      // Initialize arrays for completed and delayed tasks for sorting later
      const tasksWithDeadline: any[] = [];
      const tasksWithoutDeadline: any[] = [];

      // Consolidate counting, filtering, and sorting preparation in one loop
      filteredTasks.forEach((task) => {
        // Count completed and delayed tasks
        if (task.statusName === STATUS_NAME.COMPLETED) {
          filterValueProjectTasks.totalTaskCompleted++;
        } else if (task.statusName === STATUS_NAME.DELAYED) {
          filterValueProjectTasks.totalTaskDelay++;
        }

        // Separate tasks based on whether they have a valid deadline
        if (task.deadline && dayjs(task.deadline).isValid()) {
          tasksWithDeadline.push(task);
        } else {
          tasksWithoutDeadline.push(task);
        }
      });

      // Sort tasks with deadlines in ascending order
      tasksWithDeadline.sort((a, b) => dayjs(a.deadline).diff(b.deadline));

      // Sort tasks without deadline by createdDate asc
      tasksWithoutDeadline.sort((a, b) => dayjs(a.createdDate).diff(b.createdDate));

      // Combine the sorted tasks with and without deadlines
      const sortedTasks = filteredTasks?.some((item: any) => item.displayOrder) ? filteredTasks : [...tasksWithDeadline, ...tasksWithoutDeadline];

      // Add to newList if sorted tasks exist or no total filtering is applied
      if (sortedTasks.length > 0 || !totalFiltered()) {
        newList.push({
          ...item,
          rowKey: item.id,
          projectTasks: sortedTasks,
          summaryData: renderDataTask(filteredTasks)
        });
      }
    });
    return {
      totalTask: totalTask ?? 0,
      newList: newList ?? [],
      totalTaskCompleted: filterValueProjectTasks.totalTaskCompleted ?? 0,
      includeTaskDelay: !!filterValueProjectTasks.totalTaskDelay || false
    };
  };

  const onFilterAndsetExpandedKeys = () => {
    const listAllKey = tasksList.filter((process) => !isTravelProcess(process)).map((item) => item.id);
    setExpandedKeys(listAllKey);
  };

  return (
    <FormProvider {...formFilter}>
      <div className='task-list'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center'>
            <div className='text-20 font-bold mr-[12px]'>{t('project_detail:task_list:title')}</div>
            {!!overviewAllTask.totalTask && !totalFiltered() && (
              <>
                <div className='text-16 font-semibold mr-[8px]'>
                  {overviewAllTask.totalTaskCompleted}/{overviewAllTask.totalTask} {t('project_detail:task_list:completion')}
                </div>
                <Progress
                  className='m-0 w-[208px]'
                  percent={(overviewAllTask.totalTaskCompleted / overviewAllTask.totalTask) * 100}
                  strokeColor='#36B368'
                  showInfo={false}
                />
              </>
            )}
          </div>
          {!!listProjectProcess.length && (
            <div className='flex items-center gap-[16px]'>
              <TaskFilter
                formFilter={formFilter}
                totalFiltered={totalFiltered()}
                picOptions={picOptions}
                setExpandedKeys={onFilterAndsetExpandedKeys}
              />
            </div>
          )}
        </div>
        {overviewAllTask.includeTaskDelay && !totalFiltered() && (
          <div className='warning-delay'>
            <span className='icon-infor'>
              <InforIcon />
            </span>
            <span className='task-overdue'>
              {t('project_detail:task_list:task_overdue')}{' '}
              <span className='list-task-delay' onClick={setFilterTaskDelay}>
                {t('project_detail:task_list:list_task_delay')}
              </span>
            </span>
          </div>
        )}
        <RenderTagTaskList form={formFilter} picOptions={picOptions} formValues={formValues} totalFiltered={totalFiltered()} />
        <BaseTable
          rowKey={'rowKey'}
          columns={generateProcessColumns()}
          dataSource={tasksList}
          className='text-[#12212E] font-semibold task-list-table overflow-auto'
          pagination={false}
          showHeader={false}
          rowClassName={(record, index) => handleRowClass(record, index)}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpand: handleOnExpand,
            expandedRowRender: (record) => {
              return (
                <TaskTable
                  listTask={record?.projectTasks}
                  fetchData={fetchData}
                  processId={record.id}
                  processName={record.processName}
                  setTasksList={setTasksList}
                  tasksList={tasksList}
                />
              );
            },
            rowExpandable: (record) => record.processName !== PROCESS_TRAVEL_NAME
          }}
        />
      </div>
    </FormProvider>
  );
};

export default TaskList;
